import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { GitBranch, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { api } from '../utils/api';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMsg('Invalid verification link — no token provided.');
      return;
    }
    const verify = async () => {
      try {
        const res = await api.post('/auth/verify-email', { token });
        if (res.ok) {
          setStatus('success');
        } else {
          setStatus('error');
          setErrorMsg(res.data?.detail || 'Verification failed.');
        }
      } catch {
        setStatus('error');
        setErrorMsg('Something went wrong. Please try again.');
      }
    };
    verify();
  }, [token]);

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <div className="flex items-center gap-2 mb-8">
        <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
          <GitBranch className="w-5 h-5 text-primary-foreground" />
        </div>
        <span className="font-bold text-lg">AutomataLab</span>
      </div>

      <Card className="w-full max-w-md" data-testid="verify-email-card">
        {status === 'verifying' && (
          <CardHeader className="text-center">
            <div className="flex justify-center mb-2">
              <Loader2 className="w-12 h-12 animate-spin text-primary" />
            </div>
            <CardTitle>Verifying Email</CardTitle>
            <CardDescription>Please wait...</CardDescription>
          </CardHeader>
        )}

        {status === 'success' && (
          <>
            <CardHeader className="text-center">
              <div className="flex justify-center mb-2">
                <CheckCircle className="w-12 h-12 text-green-500" />
              </div>
              <CardTitle>Email Updated</CardTitle>
              <CardDescription>Your email has been changed successfully. Please log in again with your new email.</CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <Link to="/">
                <Button data-testid="back-to-login-btn">Back to Login</Button>
              </Link>
            </CardContent>
          </>
        )}

        {status === 'error' && (
          <>
            <CardHeader className="text-center">
              <div className="flex justify-center mb-2">
                <XCircle className="w-12 h-12 text-destructive" />
              </div>
              <CardTitle>Verification Failed</CardTitle>
              <CardDescription>{errorMsg}</CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <Link to="/settings">
                <Button variant="outline" data-testid="back-to-settings-btn">Back to Settings</Button>
              </Link>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
}
